import classNames from "classnames";
import { Nullable } from "primereact/ts-helpers";
import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { WSField } from "../WSField/WSField";
import { WSInputContainer, WSInputContainerAction } from "../WSInputContainer/WSInputContainer";
import { WSAvatar } from "../common/WSAvatar/WSAvatar.component";
import { WSText } from "../core/WSText/WSText.component";
import { WSIcon } from "../core/WSIcon/WSIcon.component";
import { WSFlexBox } from "../layout/WSFlexBox/WSFlexBox.component";
import { Menu } from "./Menu";
import styles from "./WSSelect.module.scss";
import { WSSelectProps } from "./types";
import { getDataTestId } from "../../../utils/getDataTestId";
import { WSElement } from "../WSElement/WSElement.component";

export type * from "./types";

const DEFAULT_MAX_VISIBLE_PILLS = 2;

export const WSSelect: React.FC<WSSelectProps> = (props) => {
  const {
    name,
    options: initialOptions,
    mode,
    value,
    onChange,
    disabled,
    placeholder,
    onBlur,
    onFocus,
    externalSearchText,
    onExternalSearchChange,
    internalSearch,
    isListLoading,
    onReachMenuEnd,
    menuFooterText,
    menuFooterAction,
    autoFocus,
    autoComplete,
    hideClearAction,
    emptyState,
    maxVisiblePills = DEFAULT_MAX_VISIBLE_PILLS,
    ...fieldProps
  } = props;

  const isMultiple = mode === "multiple";
  const defaultPlaceholder = isMultiple
    ? "Select option(s)"
    : "Select an option";
  const resolvedPlaceholder = placeholder ?? defaultPlaceholder;

  const ref = useRef<HTMLDivElement>(null);

  const hasValue = useMemo(() => {
    if (isMultiple) {
      return Array.isArray(value) && value.length > 0;
    }
    return !!value;
  }, [value, isMultiple]);

  const clearAction = useMemo((): WSInputContainerAction | undefined => {
    if (!hasValue || hideClearAction || disabled) return undefined;

    return {
      icon: "exit",
      onClick: () => {
        onChange(null as never);
      }
    };
  }, [hasValue, onChange, hideClearAction, disabled]);

  const selectedOption = useMemo(
    () =>
      !isMultiple
        ? initialOptions.find((option) => option.value === value)
        : undefined,
    [initialOptions, value, isMultiple]
  );

  const selectedOptions = useMemo(() => {
    if (!isMultiple || !Array.isArray(value)) return [];
    return value
      .map((v) => initialOptions.find((option) => option.value === v))
      .filter(
        (opt): opt is NonNullable<typeof opt> => opt !== undefined
      );
  }, [initialOptions, value, isMultiple]);

  // Search state
  const [internalSearchText, internalSetSearchText] = useState("");
  const searchText = useMemo(
    () => (internalSearch ? internalSearchText : externalSearchText),
    [externalSearchText, internalSearch, internalSearchText]
  );
  const onSearchChange = useMemo(
    () => (internalSearch ? internalSetSearchText : onExternalSearchChange),
    [onExternalSearchChange, internalSearch]
  );

  // Sort and filter options
  const options = useMemo(() => {
    let filteredOptions = initialOptions.filter((option) => !option.delisted);

    if (internalSearch && internalSearchText) {
      filteredOptions = filteredOptions.filter((option) =>
        [option.label, option.searchText, ...(option.labels || [])]
          .map((str) => str?.toLowerCase().trim())
          .some((str) => str?.includes(internalSearchText.toLowerCase().trim()))
      );
    }

    // Only reorder for single-select
    if (isMultiple || !selectedOption || !!searchText) {
      return filteredOptions;
    }

    const restOptions = filteredOptions.filter(
      (option) => option.value !== selectedOption.value
    );
    return [selectedOption, ...restOptions];
  }, [
    initialOptions,
    internalSearch,
    internalSearchText,
    searchText,
    selectedOption,
    isMultiple
  ]);

  const [isMenuVisible, setIsMenuVisible] = useState(false);

  useEffect(() => {
    if (autoFocus) {
      setIsMenuVisible(true);
    }
  }, [autoFocus]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (disabled) return;

      if (isMenuVisible && event.key === "Escape") {
        setIsMenuVisible(false);
      } else if (!isMenuVisible && event.key === "ArrowDown") {
        setIsMenuVisible(true);
      }
    },
    [isMenuVisible, disabled]
  );

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    const node = ref.current;
    node.addEventListener("keydown", handleKeyDown);

    return () => {
      node.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  const manualInputRef = useRef<HTMLInputElement>(null);

  const handleManualInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (isMultiple) return;

      const inputValue = event.target.value.toLowerCase();
      const matchingOptions = options.filter(
        (option) =>
          option.value.toLowerCase() === inputValue ||
          option.label.toLowerCase() === inputValue ||
          option.searchText?.toLowerCase() === inputValue
      );

      if (matchingOptions.length === 1) {
        (onChange as (value: string | null) => void)(
          matchingOptions[0].value
        );
      }
    },
    [onChange, options, isMultiple]
  );

  const handlePillDismiss = useCallback(
    (valueToRemove: string) => {
      if (!isMultiple || !Array.isArray(value)) return;
      const next = value.filter((v) => v !== valueToRemove);
      (onChange as (value: string[] | null) => void)(
        next.length > 0 ? next : null
      );
    },
    [onChange, value, isMultiple]
  );

  const testIdBase = getDataTestId("select", name, fieldProps.label);

  const renderInputContent = (): React.ReactNode => {
    if (isMultiple) {
      if (selectedOptions.length === 0) {
        return (
          <WSText color="gray400">{resolvedPlaceholder}</WSText>
        );
      }

      const showCollapsed = selectedOptions.length > maxVisiblePills;

      if (showCollapsed) {
        return (
          <WSElement className={styles.pills}>
            <WSElement
              className={styles.pill}
              data-testid={
                testIdBase
                  ? `${testIdBase}PillCollapsed`
                  : "selectPillCollapsed"
              }
            >
              {selectedOptions.length} items selected
            </WSElement>
          </WSElement>
        );
      }

      return (
        <WSElement className={styles.pills}>
          {selectedOptions.map((opt) => (
            <WSElement
              key={opt.value}
              className={styles.pill}
              data-testid={
                testIdBase
                  ? `${testIdBase}Pill-${opt.value}`
                  : `selectPill-${opt.value}`
              }
            >
              {opt.label}
              {!disabled && (
                <WSElement
                  className={styles.pillDismiss}
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePillDismiss(opt.value);
                  }}
                  data-testid={
                    testIdBase
                      ? `${testIdBase}PillDismiss-${opt.value}`
                      : `selectPillDismiss-${opt.value}`
                  }
                >
                  <WSIcon name="exit" size="XS" color="gray400" />
                </WSElement>
              )}
            </WSElement>
          ))}
        </WSElement>
      );
    }

    // Single-select display
    if (selectedOption) {
      return (
        <WSFlexBox wrap="nowrap" alignItems="center" gap="S">
          {selectedOption.icon ? (
            <WSIcon name={selectedOption.icon} size="S" color="gray600" />
          ) : selectedOption.avatar ? (
            <WSAvatar
              color="white"
              colorBackground="gray500"
              size="S"
              {...selectedOption.avatar}
            />
          ) : null}
          <WSText color="gray600" singleLine>
            {selectedOption.label}
          </WSText>
        </WSFlexBox>
      );
    }

    if (resolvedPlaceholder) {
      return <WSText color="gray400">{resolvedPlaceholder}</WSText>;
    }

    return null;
  };

  return (
    <WSField
      {...fieldProps}
      className={classNames(
        styles.select,
        fieldProps.className,
        disabled && styles.disabled
      )}
      as="div"
    >
      <WSInputContainer
        status={fieldProps.status}
        disabled={disabled}
        iconRight={isMenuVisible ? "caret-up" : "caret-down"}
        inputAction={clearAction}
        onClick={disabled ? undefined : () => setIsMenuVisible((prev) => !prev)}
        ref={ref}
        tabIndex={0}
        className={styles.inputContainer}
        data-testid={testIdBase}
      >
        <input
          role="combobox"
          autoComplete={autoComplete}
          className={styles.manualInput}
          ref={manualInputRef}
          disabled={disabled}
          type="text"
          name={name}
          onChange={handleManualInputChange}
          data-testid={
            testIdBase ? `${testIdBase}Input` : "selectInput"
          }
        />
        {renderInputContent()}
      </WSInputContainer>

      {isMenuVisible && (
        <Menu
          {...(isMultiple
            ? {
                mode: "multiple" as const,
                value: value as Nullable<string[]>,
                onChange: onChange as (value: Nullable<string[]>) => void
              }
            : {
                mode: "single" as const,
                value: value as Nullable<string>,
                onChange: onChange as (value: Nullable<string>) => void
              })}
          options={options}
          onClose={() => {
            setIsMenuVisible(false);
            onSearchChange?.("");
          }}
          targetRef={ref}
          searchText={searchText}
          onSearchChange={onSearchChange}
          isListLoading={isListLoading}
          onReachEnd={onReachMenuEnd}
          footerText={menuFooterText}
          footerAction={menuFooterAction}
          emptyState={emptyState}
        />
      )}
    </WSField>
  );
};
